import axios from 'axios';

// store/modules/Rules.js
export default {
    namespaced: true,

    state: {
        // Search state
        searchQuery: '',
        searchResults: [],
        searchLoading: false,
        searchError: null,
        hasSearched: false,

        // Pagination
        currentPage: 1,
        perPage: 10,
        totalResults: 0,
        totalPages: 0,

        // Filters
        selectedDocument: null,
        selectedSection: null,
        minScore: 0.7,
        appliedFilters: [],

        // Available data
        availableDocuments: [],
        documentsLoading: false,
        documentsError: null,

        // Search metadata
        searchTime: null,
        lastSearchParams: null,

        // Cache
        searchCache: new Map(),
        cacheExpiry: 5 * 60 * 1000, // 5 minutes

        // UI state
        showAdvancedFilters: false,
        showSuggestions: true,

        // Selected rule details
        selectedRule: null,
        ruleDetailsLoading: false,
        ruleDetailsError: null,
    },

    getters: {
        // Search state getters
        isSearching: state => state.searchLoading,
        hasResults: state => state.searchResults.length > 0,
        hasError: state => !!state.searchError,

        // Filter getters
        hasActiveFilters: state => {
            return state.selectedDocument || state.selectedSection || state.minScore !== 0.7 || state.perPage !== 10;
        },

        activeFiltersCount: (state, _getters) => {
            let count = 0;
            if (state.selectedDocument) {
                count++;
            }
            if (state.selectedSection) {
                count++;
            }
            if (state.minScore !== 0.7) {
                count++;
            }
            if (state.perPage !== 10) {
                count++;
            }
            return count;
        },

        // Search params getter
        searchParams: state => {
            const params = {
                query: state.searchQuery.trim(),
                page: state.currentPage,
                per_page: state.perPage,
                min_score: state.minScore,
            };

            if (state.selectedDocument) {
                params.document_id = state.selectedDocument;
            }

            if (state.selectedSection) {
                params.section = state.selectedSection.trim();
            }

            return params;
        },

        // Cache key generator
        cacheKey: (_state, getters) => {
            return JSON.stringify(getters.searchParams);
        },

        // Pagination getters
        startItem: state => {
            return (state.currentPage - 1) * state.perPage + 1;
        },

        endItem: state => {
            return Math.min(state.currentPage * state.perPage, state.totalResults);
        },
    },

    actions: {
        // Initialize store
        async initialize({ dispatch }) {
            await dispatch('loadAvailableDocuments');
        },

        // Load available documents for filtering
        async loadAvailableDocuments({ commit }) {
            commit('SET_DOCUMENTS_LOADING', true);
            commit('SET_DOCUMENTS_ERROR', null);

            try {
                const response = await axios.get('/api/rules');

                if (response.data.success) {
                    commit('SET_AVAILABLE_DOCUMENTS', response.data.data.documents || []);
                } else {
                    throw new Error(response.data.error || 'Failed to load documents');
                }
            } catch (error) {
                console.error('Failed to load available documents:', error);
                commit('SET_DOCUMENTS_ERROR', error.response?.data?.message || error.message);
            } finally {
                commit('SET_DOCUMENTS_LOADING', false);
            }
        },

        // Perform search
        async search({ commit, getters, state }, params = null) {
            const searchParams = params || getters.searchParams;
            const cacheKey = JSON.stringify(searchParams);

            // Check cache first
            if (state.searchCache.has(cacheKey)) {
                const cached = state.searchCache.get(cacheKey);
                if (Date.now() - cached.timestamp < state.cacheExpiry) {
                    commit('SET_SEARCH_RESULTS_FROM_CACHE', cached);
                    return;
                }
            }

            commit('SET_SEARCH_LOADING', true);
            commit('SET_SEARCH_ERROR', null);

            const startTime = performance.now();

            try {
                const response = await axios.get('/api/rules/search', { params: searchParams });

                if (response.data.success) {
                    const endTime = performance.now();
                    const searchTime = (endTime - startTime) / 1000;

                    const resultData = {
                        results: response.data.data.results || [],
                        totalResults: response.data.data.total || 0,
                        currentPage: response.data.data.current_page || 1,
                        perPage: response.data.data.per_page || 10,
                        totalPages: response.data.data.last_page || 1,
                        searchTime,
                        searchParams,
                    };

                    // Cache the results
                    state.searchCache.set(cacheKey, {
                        ...resultData,
                        timestamp: Date.now(),
                    });

                    commit('SET_SEARCH_RESULTS', resultData);
                    commit('SET_HAS_SEARCHED', true);
                } else {
                    throw new Error(response.data.error || 'Search failed');
                }
            } catch (error) {
                console.error('Search failed:', error);
                commit('SET_SEARCH_ERROR', error.response?.data?.message || error.message);
                commit('SET_SEARCH_RESULTS', {
                    results: [],
                    totalResults: 0,
                    currentPage: 1,
                    perPage: state.perPage,
                    totalPages: 0,
                    searchTime: null,
                    searchParams,
                });
            } finally {
                commit('SET_SEARCH_LOADING', false);
            }
        },

        // Update search query
        updateSearchQuery({ commit }, query) {
            commit('SET_SEARCH_QUERY', query);
        },

        // Update filters
        updateFilters({ commit }, filters) {
            if (filters.selectedDocument !== undefined) {
                commit('SET_SELECTED_DOCUMENT', filters.selectedDocument);
            }
            if (filters.selectedSection !== undefined) {
                commit('SET_SELECTED_SECTION', filters.selectedSection);
            }
            if (filters.minScore !== undefined) {
                commit('SET_MIN_SCORE', filters.minScore);
            }
            if (filters.perPage !== undefined) {
                commit('SET_PER_PAGE', filters.perPage);
            }
        },

        // Clear filters
        clearFilters({ commit }) {
            commit('SET_SELECTED_DOCUMENT', null);
            commit('SET_SELECTED_SECTION', null);
            commit('SET_MIN_SCORE', 0.7);
            commit('SET_PER_PAGE', 10);
            commit('SET_CURRENT_PAGE', 1);
        },

        // Clear search
        clearSearch({ commit }) {
            commit('SET_SEARCH_QUERY', '');
            commit('SET_SEARCH_RESULTS', {
                results: [],
                totalResults: 0,
                currentPage: 1,
                perPage: 10,
                totalPages: 0,
                searchTime: null,
                searchParams: null,
            });
            commit('SET_HAS_SEARCHED', false);
            commit('SET_SEARCH_ERROR', null);
        },

        // Change page
        async changePage({ commit, dispatch }, page) {
            commit('SET_CURRENT_PAGE', page);
            await dispatch('search');
        },

        // Get rule details
        async getRuleDetails({ commit }, ruleId) {
            commit('SET_RULE_DETAILS_LOADING', true);
            commit('SET_RULE_DETAILS_ERROR', null);

            try {
                const response = await axios.get(`/api/rules/${ruleId}`);

                if (response.data.success) {
                    commit('SET_SELECTED_RULE', response.data.data);
                } else {
                    throw new Error(response.data.error || 'Failed to load rule details');
                }
            } catch (error) {
                console.error('Failed to load rule details:', error);
                commit('SET_RULE_DETAILS_ERROR', error.response?.data?.message || error.message);
            } finally {
                commit('SET_RULE_DETAILS_LOADING', false);
            }
        },

        // Clear cache
        clearCache({ state }) {
            state.searchCache.clear();
        },

        // Toggle UI states
        toggleAdvancedFilters({ commit, state }) {
            commit('SET_SHOW_ADVANCED_FILTERS', !state.showAdvancedFilters);
        },

        toggleSuggestions({ commit, state }) {
            commit('SET_SHOW_SUGGESTIONS', !state.showSuggestions);
        },
    },

    mutations: {
        // Search mutations
        SET_SEARCH_QUERY(state, query) {
            state.searchQuery = query;
        },

        SET_SEARCH_LOADING(state, loading) {
            state.searchLoading = loading;
        },

        SET_SEARCH_ERROR(state, error) {
            state.searchError = error;
        },

        SET_SEARCH_RESULTS(state, data) {
            state.searchResults = data.results;
            state.totalResults = data.totalResults;
            state.currentPage = data.currentPage;
            state.perPage = data.perPage;
            state.totalPages = data.totalPages;
            state.searchTime = data.searchTime;
            state.lastSearchParams = data.searchParams;
        },

        SET_SEARCH_RESULTS_FROM_CACHE(state, cached) {
            state.searchResults = cached.results;
            state.totalResults = cached.totalResults;
            state.currentPage = cached.currentPage;
            state.perPage = cached.perPage;
            state.totalPages = cached.totalPages;
            state.searchTime = cached.searchTime;
            state.lastSearchParams = cached.searchParams;
            state.searchLoading = false;
            state.searchError = null;
        },

        SET_HAS_SEARCHED(state, hasSearched) {
            state.hasSearched = hasSearched;
        },

        // Pagination mutations
        SET_CURRENT_PAGE(state, page) {
            state.currentPage = page;
        },

        SET_PER_PAGE(state, perPage) {
            state.perPage = perPage;
        },

        // Filter mutations
        SET_SELECTED_DOCUMENT(state, document) {
            state.selectedDocument = document;
        },

        SET_SELECTED_SECTION(state, section) {
            state.selectedSection = section;
        },

        SET_MIN_SCORE(state, score) {
            state.minScore = score;
        },

        SET_APPLIED_FILTERS(state, filters) {
            state.appliedFilters = filters;
        },

        // Documents mutations
        SET_AVAILABLE_DOCUMENTS(state, documents) {
            state.availableDocuments = documents;
        },

        SET_DOCUMENTS_LOADING(state, loading) {
            state.documentsLoading = loading;
        },

        SET_DOCUMENTS_ERROR(state, error) {
            state.documentsError = error;
        },

        // Rule details mutations
        SET_SELECTED_RULE(state, rule) {
            state.selectedRule = rule;
        },

        SET_RULE_DETAILS_LOADING(state, loading) {
            state.ruleDetailsLoading = loading;
        },

        SET_RULE_DETAILS_ERROR(state, error) {
            state.ruleDetailsError = error;
        },

        // UI mutations
        SET_SHOW_ADVANCED_FILTERS(state, show) {
            state.showAdvancedFilters = show;
        },

        SET_SHOW_SUGGESTIONS(state, show) {
            state.showSuggestions = show;
        },
    },
};
